import { useStableMediaSrc } from "@/lib/hooks/shared/useStableMediaSrc";
import {
  CAPTION_BUBBLE_PADDING_PERCENT,
  CAPTION_EDGE_INSET_PERCENT,
  CAPTION_HORIZONTAL_INSET_PERCENT,
  CAPTION_LINE_HEIGHT_RATIO,
  DEFAULT_SUBTITLE_POSITION,
  DEFAULT_SUBTITLE_PRESET,
  SUBTITLE_FONT_SIZE_REFERENCE_WIDTH,
  SUBTITLE_PRESETS,
  type SubtitlePosition,
  type SubtitlePreset,
  type SubtitleWord,
  buildSyllableSegments,
  clampSubtitleFontScale,
  clampSubtitleOffset,
  computeCues,
  findVisibleCue,
  getSubtitleFontSize,
  isSyllableCue,
} from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiExclamation, HiPause, HiPlay, HiPlus, HiX } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import { getSubtitleOverlayStyle } from "./subtitleOverlayStyle";

interface SubtitleEditorProps {
  src: string;
  subtitles: SubtitleWord[];
  position?: string | null | undefined;
  preset?: string | null | undefined;
  fontScale?: number | null | undefined;
  offset?: number | null | undefined;
  isSaving: boolean;
  /** Timings only: the word list is fixed, so it cannot be retyped, added to, or deleted from. */
  lockWords?: boolean;
  onSave: (subtitles: SubtitleWord[]) => void;
  onClose: () => void;
}

function fmtTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1).padStart(4, "0");
  return `${m}:${sec}`;
}

const MIN_DURATION = 0.1; // Minimum seconds a word can last

// Free-form while focused and committed on blur or Enter: clamping each keystroke makes a
// decimal impossible to type, since "1." parses as 1 and snaps the field.
function TimeInput({
  label,
  color,
  value,
  onCommit,
}: {
  label: string;
  color: string;
  value: number;
  onCommit: (seconds: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const parsed = Number.parseFloat(draft);
    setDraft(null);
    if (Number.isFinite(parsed)) onCommit(parsed);
  };

  return (
    <label className={`flex items-center gap-1 text-[11px] ${color}`}>
      {label}
      <input
        type="number"
        step={0.05}
        min={0}
        value={draft ?? value.toFixed(2)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Escape") setDraft(null);
        }}
        className="w-16 px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700 text-white font-mono text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <span className="text-gray-500">s</span>
    </label>
  );
}

const CUE_PALETTE = ["#2dd4bf", "#a78bfa", "#fbbf24", "#f87171", "#60a5fa", "#34d399"];
const cueColorFor = (ci: number | null | undefined) =>
  ci != null ? (CUE_PALETTE[ci % CUE_PALETTE.length] ?? "#9ca3af") : "#9ca3af";

export function SubtitleEditor({
  src,
  subtitles,
  position: positionProp,
  preset: presetProp,
  fontScale: fontScaleProp,
  offset: offsetProp,
  isSaving,
  lockWords = false,
  onSave,
  onClose,
}: SubtitleEditorProps) {
  const position = (positionProp as SubtitlePosition) ?? DEFAULT_SUBTITLE_POSITION;
  const preset = (presetProp as SubtitlePreset) ?? DEFAULT_SUBTITLE_PRESET;
  const captionStyle = SUBTITLE_PRESETS[preset] ?? SUBTITLE_PRESETS[DEFAULT_SUBTITLE_PRESET];

  // Read-only here — the caption style is authored on the player's control bar. The editor
  // mirrors it so the timings are adjusted against the captions as they will actually appear.
  const fontScale = clampSubtitleFontScale(fontScaleProp);
  const offset = clampSubtitleOffset(offsetProp);

  const overlayPositionStyle = getSubtitleOverlayStyle(
    position,
    offset,
    position === "top" ? CAPTION_EDGE_INSET_PERCENT.top : CAPTION_EDGE_INSET_PERCENT.bottom
  );
  const stableSrc = useStableMediaSrc(src);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const wordListRef = useRef<HTMLDivElement>(null);
  const videoPanelRef = useRef<HTMLDivElement>(null);
  const activeWordRef = useRef<HTMLDivElement>(null);
  // Measures the stable modal width (not the video area itself, which is about to be resized
  // from this same measurement — using it as its own reference would shrink on every resize).
  const modalRef = useRef<HTMLDivElement>(null);
  // The video's actual rendered box, computed in px once its real dimensions are known — the
  // caption preview is anchored to this box rather than to a container that only approximates
  // the video's aspect ratio via CSS, so it can't end up positioned over letterboxed black space.
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);

  const referenceWidth = renderedSize?.width ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH;
  const captionFontSize = getSubtitleFontSize(captionStyle.fontSize, fontScale, referenceWidth);
  const captionHorizontalInset = referenceWidth * (CAPTION_HORIZONTAL_INSET_PERCENT / 100);
  const captionBubblePadding = referenceWidth * (CAPTION_BUBBLE_PADDING_PERCENT / 100);

  const computeRenderedSize = useCallback(() => {
    const v = videoRef.current;
    const panel = videoPanelRef.current;
    if (!v?.videoWidth || !v?.videoHeight || !panel) return;
    const ratio = v.videoWidth / v.videoHeight;
    const availableWidth = panel.clientWidth;
    // Fits the clip to its own column. clientHeight is 0 until the row has laid out, so fall back
    // to a share of the viewport rather than collapsing the preview to nothing.
    const maxHeight = panel.clientHeight || window.innerHeight * 0.5;
    const heightAtFullWidth = availableWidth / ratio;
    if (heightAtFullWidth <= maxHeight) {
      setRenderedSize({ width: availableWidth, height: heightAtFullWidth });
    } else {
      setRenderedSize({ width: maxHeight * ratio, height: maxHeight });
    }
  }, []);

  // The column has no height on the first measure, so the clip would be sized against the
  // viewport fallback and overflow into the scrubber below it until something else re-measured.
  useEffect(() => {
    const panel = videoPanelRef.current;
    if (!panel) return;

    const observer = new ResizeObserver(computeRenderedSize);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [computeRenderedSize]);

  const [words, setWords] = useState<SubtitleWord[]>(
    subtitles.map(({ word, start, end, cueIndex }) => ({
      word,
      start,
      end,
      cueIndex: cueIndex ?? null,
    }))
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    subtitles.length > 0 ? 0 : null
  );
  const [dragging, setDragging] = useState<"start" | "end" | null>(null);

  const overlappingIndices = useMemo(() => {
    const overlaps = new Set<number>();
    for (let i = 1; i < words.length; i++) {
      const current = words[i];
      const prev = words[i - 1];
      if (!current || !prev) continue;
      if (current.start < prev.end) {
        overlaps.add(i);
        overlaps.add(i - 1);
      }
    }
    return overlaps;
  }, [words]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const timeToPercent = (t: number) => (duration > 0 ? (t / duration) * 100 : 0);

  const xToTime = useCallback(
    (clientX: number) => {
      const bar = timelineRef.current;
      if (!bar || duration === 0) return 0;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return ratio * duration;
    },
    [duration]
  );

  const seekTo = useCallback(
    (time: number) => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(v.currentTime);
    },
    [duration]
  );

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) v.pause();
    else v.play();
  }, [isPlaying]);

  // ── Keyboard Controls ─────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (["INPUT", "TEXTAREA"].includes((e.target as HTMLElement).tagName)) return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [togglePlay]);

  // Starts have to stay ascending: the player highlights the last word whose start has been
  // reached and stops scanning at the first one past the playhead, so an out-of-order start
  // silently kills the highlight for every word after it. Ends are left free — they may run
  // past the next word, which only keeps the earlier word lit through the overlap.
  const setEdge = useCallback(
    (index: number, edge: "start" | "end", time: number) => {
      setWords((prev) => {
        const w = prev[index];
        if (!w) return prev;

        let start = w.start;
        let end = w.end;

        if (edge === "start") {
          const earliest = Math.max(prev[index - 1]?.start ?? 0, 0);
          const latest = Math.min(prev[index + 1]?.start ?? duration, w.end - MIN_DURATION);
          start = Math.max(earliest, Math.min(time, latest));
        } else {
          end = Math.max(w.start + MIN_DURATION, Math.min(time, duration));
        }

        if (start === w.start && end === w.end) return prev;

        return prev.map((item, i) => (i === index ? { ...item, start, end } : item));
      });
    },
    [duration]
  );

  // ── Drag logic ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (dragging === null || selectedIndex === null) return;

    const onMove = (e: MouseEvent) => {
      const t = xToTime(e.clientX);
      seekTo(t);
      setEdge(selectedIndex, dragging, t);
    };

    const onUp = () => setDragging(null);

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging, selectedIndex, xToTime, seekTo, setEdge]);

  // ── Auto-scroll active word during playback ───────────────────────────────
  const activeWordIndex = words.findIndex((w) => currentTime >= w.start && currentTime <= w.end);

  // Preview the captions the way the player will show them — one cue at a time, with the same
  // lead-in — so timings are adjusted against what the student actually sees. Derived from the
  // words being edited, so it updates live as they are dragged.
  const cues = useMemo(() => computeCues(words), [words]);
  const visibleCue = findVisibleCue(cues, currentTime);

  // Highlighting stays locked to the audio, so during the lead-in no word is lit yet — mirrors
  // VideoPlayer.tsx so a pronunciation slide's syllable breakdown previews the same way here.
  const activeWordIndexInVisibleCue = visibleCue
    ? visibleCue.findIndex((w) => currentTime >= w.start && currentTime <= w.end)
    : -1;
  const fullWordFromCue0 = cues[0]?.[0]?.word ?? "";
  const syllableSegments =
    visibleCue && isSyllableCue(visibleCue, fullWordFromCue0)
      ? buildSyllableSegments(
          fullWordFromCue0,
          visibleCue.map((w) => w.word)
        )
      : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentTime and words determine activeWordIndex, which controls which element activeWordRef points to
  useEffect(() => {
    if (isPlaying && activeWordRef.current) {
      activeWordRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [currentTime, words, isPlaying]);

  // ── Word mutations (with boundary awareness) ──────────────────────────────
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragging) return;
    seekTo(xToTime(e.clientX));
  };

  const updateText = (index: number, value: string) =>
    setWords((prev) => prev.map((w, i) => (i === index ? { ...w, word: value } : w)));

  const updateCueIndex = (index: number, delta: number) =>
    setWords((prev) =>
      prev.map((w, i) => {
        if (i !== index) return w;
        const current = w.cueIndex ?? 0;
        return { ...w, cueIndex: Math.max(0, current + delta) };
      })
    );

  const deleteWord = (index: number) => {
    setWords((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const insertAfter = (index: number) => {
    const ref = words[index];
    const defaultDuration = 0.5;
    const nextStart = index < words.length - 1 ? words[index + 1]?.start : duration;

    // Check if there is enough space to insert a word
    const availableSpace = nextStart ? nextStart - (ref?.end || 0) : 0;
    if (availableSpace <= MIN_DURATION) return;

    const newWord: SubtitleWord = {
      word: "",
      start: ref?.end || 0,
      end: (ref?.end || 0) + Math.min(defaultDuration, availableSpace),
    };
    setWords((prev) => [...prev.slice(0, index + 1), newWord, ...prev.slice(index + 1)]);
    setSelectedIndex(index + 1);
    seekTo(newWord.start);
  };

  const addAtEnd = () => {
    const lastWord = words[words.length - 1];
    const startOffset = lastWord ? Math.max(currentTime, lastWord.end) : currentTime;
    const endOffset = Math.min(duration, startOffset + 0.5);

    if (endOffset <= startOffset + MIN_DURATION) return; // no room left

    const newWord: SubtitleWord = {
      word: "",
      start: startOffset,
      end: endOffset,
    };
    setWords((prev) => [...prev, newWord]);
    setSelectedIndex(words.length);
  };

  const cueGroupBoundaries = useMemo(() => {
    const boundaries = new Set<number>();
    for (let i = 1; i < words.length; i++) {
      if (words[i]?.cueIndex !== words[i - 1]?.cueIndex) boundaries.add(i);
    }
    return boundaries;
  }, [words]);

  // ── Derived State for Timeline Boundaries ─────────────────────────────────
  const selected = selectedIndex !== null ? (words[selectedIndex] ?? null) : null;
  const editableFrom =
    selectedIndex !== null && selectedIndex > 0 ? words[selectedIndex - 1]?.start : 0;

  // ensure start/end handles are always at least MIN_HANDLE_GAP_PCT apart
  const MIN_HANDLE_GAP_PCT = 1.5;
  const rawStartPct = selected ? timeToPercent(selected.start) : 0;
  const rawEndPct = selected ? timeToPercent(selected.end) : 0;
  const missingGap = Math.max(0, MIN_HANDLE_GAP_PCT - (rawEndPct - rawStartPct));
  const spread = missingGap / 2;
  const handleStartPct = rawStartPct - spread;
  const handleEndPct = rawEndPct + spread;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl flex flex-col h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        // Stops the drag handles' pointerdown from bubbling to the card's Sortable behind it.
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-800 shrink-0 bg-gray-50 dark:bg-gray-900/50">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Subtitle Editor
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Press{" "}
              <kbd className="bg-gray-200 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-700 dark:text-gray-300">
                Space
              </kbd>{" "}
              to play/pause
            </p>
          </div>
          <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <MdClose className="w-5 h-5 text-gray-500 hover:text-gray-900 dark:hover:text-white" />
          </IconButton>
        </div>

        {/* Body — word list beside the clip rather than under it: a portrait clip would
            otherwise take the full width's worth of height and leave room for a few words. */}
        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 min-w-0 flex flex-col">
            {/* Word list */}
            <div
              ref={wordListRef}
              className="flex-1 min-h-0 overflow-y-auto px-4 py-2 space-y-1 bg-gray-50 dark:bg-gray-900/30"
            >
              {words.map((w, i) => {
                const isSelected = selectedIndex === i;
                const isCurrentlyPlaying = i === activeWordIndex;
                const isOverlapping = overlappingIndices.has(i);
                const nextStart = i < words.length - 1 ? words[i + 1]?.start : duration;
                const canInsertAfter = nextStart !== undefined && nextStart - w.end > MIN_DURATION;

                return (
                  <div key={`${i}-${w.start}`}>
                    {!lockWords && cueGroupBoundaries.has(i) && (
                      <div className="flex items-center gap-2 my-1 px-1">
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                          Cue {w.cueIndex ?? "–"}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                      </div>
                    )}
                    <div
                      ref={isCurrentlyPlaying ? activeWordRef : null}
                      onClick={() => {
                        setSelectedIndex(i);
                        seekTo(w.start);
                      }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-all border border-l-[3px] ${
                        isSelected
                          ? "bg-white dark:bg-gray-800 border-primary-400 shadow-sm ring-1 ring-primary-400"
                          : isCurrentlyPlaying
                            ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-900/50"
                            : isOverlapping
                              ? "bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40"
                              : "border-transparent hover:bg-white dark:hover:bg-gray-800/80 hover:border-gray-200 dark:hover:border-gray-700"
                      }`}
                      style={lockWords ? undefined : { borderLeftColor: cueColorFor(w.cueIndex) }}
                    >
                      <div className="flex flex-col w-24 shrink-0 text-left">
                        <span
                          className={`text-[10px] font-bold ${isCurrentlyPlaying ? "text-yellow-600 dark:text-yellow-500" : "text-gray-400"}`}
                        >
                          #{i + 1}
                        </span>
                        <span
                          className={`text-[10px] font-mono ${isOverlapping ? "text-amber-600 dark:text-amber-500" : "text-gray-500"}`}
                        >
                          {fmtTime(w.start)} – {fmtTime(w.end)}
                        </span>
                      </div>

                      {lockWords ? (
                        <span className="flex-1 min-w-0 px-3 py-1.5 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {w.word}
                        </span>
                      ) : (
                        <input
                          type="text"
                          value={w.word}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateText(i, e.target.value)}
                          onFocus={() => setSelectedIndex(i)}
                          placeholder="Type word..."
                          className={`flex-1 min-w-0 px-3 py-1.5 text-sm rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/50 bg-transparent text-gray-900 dark:text-gray-100 font-medium ${
                            isSelected
                              ? "bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
                              : "border border-transparent hover:border-gray-300 dark:hover:border-gray-600"
                          }`}
                        />
                      )}

                      {/* Cue group controls — visible only when selected */}
                      {isSelected && !lockWords && (
                        <div
                          className="flex items-center gap-1 shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 mr-0.5">
                            Cue
                          </span>
                          <button
                            type="button"
                            title="Move to previous cue group"
                            disabled={(w.cueIndex ?? 0) <= 0}
                            onClick={() => updateCueIndex(i, -1)}
                            className="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs leading-none"
                          >
                            −
                          </button>
                          <span
                            className="text-xs font-mono w-5 text-center font-semibold"
                            style={{ color: cueColorFor(w.cueIndex) }}
                          >
                            {w.cueIndex ?? "–"}
                          </span>
                          <button
                            type="button"
                            title="Move to next cue group"
                            onClick={() => updateCueIndex(i, 1)}
                            className="w-5 h-5 flex items-center justify-center rounded text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs leading-none"
                          >
                            +
                          </button>
                        </div>
                      )}

                      {isOverlapping && (
                        <div
                          title="Overlaps an adjacent word — the earlier word stays highlighted through the overlap"
                          className="text-amber-500 shrink-0"
                        >
                          <HiExclamation className="w-5 h-5" />
                        </div>
                      )}

                      {!lockWords && (
                        <div
                          className={`flex items-center gap-1 shrink-0 transition-opacity ${isSelected || isCurrentlyPlaying || isOverlapping ? "opacity-100" : "opacity-0 group-hover:opacity-100 hover:opacity-100"}`}
                        >
                          <button
                            type="button"
                            title={
                              canInsertAfter
                                ? "Insert word after"
                                : "No space to insert a word here"
                            }
                            disabled={!canInsertAfter}
                            onClick={(e) => {
                              e.stopPropagation();
                              insertAfter(i);
                            }}
                            className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400"
                          >
                            <HiPlus className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete word"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteWord(i);
                            }}
                            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <HiX className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {words.length === 0 &&
                (lockWords ? (
                  <p className="px-4 py-6 text-center text-xs text-gray-400">
                    No timings saved for this clip — run Generate subtitle or Fix timing to create
                    them.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={addAtEnd}
                    disabled={duration <= MIN_DURATION}
                    title={duration <= MIN_DURATION ? "Waiting for the clip to load" : undefined}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-400"
                  >
                    <span className="flex flex-col w-24 shrink-0 text-left">
                      <span className="text-[10px] font-bold">#1</span>
                      <span className="text-[10px] font-mono">{fmtTime(0)} – …</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <HiPlus className="w-4 h-4" />
                      Add word
                    </span>
                  </button>
                ))}
            </div>
          </div>

          <div className="shrink-0 w-2/5 max-w-xs border-l border-gray-200 dark:border-gray-800 flex flex-col">
            <div
              ref={videoPanelRef}
              className="flex-1 min-h-0 bg-black flex items-center justify-center overflow-hidden"
            >
              {/* Video viewport — the clip, sized to fit this column */}
              <div
                className="bg-black relative overflow-hidden max-w-full max-h-full"
                style={{ width: renderedSize?.width, height: renderedSize?.height }}
              >
                <div
                  className="relative"
                  style={
                    renderedSize
                      ? { width: renderedSize.width, height: renderedSize.height }
                      : undefined
                  }
                >
                  <div className="relative cursor-pointer group w-full h-full" onClick={togglePlay}>
                    {/* biome-ignore lint/a11y/useMediaCaption: subtitle editor — captions are the content being edited, not consumed */}
                    <video
                      ref={videoRef}
                      src={stableSrc}
                      controls={false}
                      className="w-full h-full block object-contain"
                      onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
                      onLoadedMetadata={() => {
                        setDuration(videoRef.current?.duration ?? 0);
                        computeRenderedSize();
                      }}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onEnded={() => setIsPlaying(false)}
                    />
                    <div
                      className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity ${isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePlay();
                        }}
                        className="w-14 h-14 rounded-full bg-white/90 shadow-lg hover:bg-white hover:scale-105 flex items-center justify-center transition-all"
                      >
                        {isPlaying ? (
                          <HiPause className="text-2xl text-gray-900" />
                        ) : (
                          <HiPlay className="text-2xl text-gray-900 ml-1" />
                        )}
                      </button>
                    </div>
                  </div>

                  {visibleCue && (
                    <div
                      className="absolute left-0 right-0 flex justify-center pointer-events-none"
                      style={{
                        ...overlayPositionStyle,
                        paddingLeft: captionHorizontalInset,
                        paddingRight: captionHorizontalInset,
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: captionStyle.backgroundColor,
                          // Matches VideoPlayer.tsx's chip exactly, so wrapping stays identical.
                          borderRadius: 6,
                          padding: `5px ${captionBubblePadding}px`,
                        }}
                        className="text-center"
                      >
                        {syllableSegments ? (
                          // Syllable cue — render as one continuous word, highlight per syllable
                          <span
                            style={{
                              fontSize: captionFontSize,
                              lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                              fontFamily: captionStyle.fontFamily,
                              fontWeight: captionStyle.fontWeight,
                              textShadow: captionStyle.textShadow
                                ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                                : undefined,
                              WebkitTextStroke: captionStyle.textOutline
                                ? `1.5px ${captionStyle.outlineColor}`
                                : undefined,
                            }}
                          >
                            {syllableSegments.map((seg, i) => (
                              <span
                                key={`${i}-${seg.text}`}
                                style={{
                                  color:
                                    seg.sylIdx === activeWordIndexInVisibleCue
                                      ? captionStyle.activeTextColor
                                      : captionStyle.textColor,
                                  transition: "color 0.1s",
                                }}
                              >
                                {seg.text}
                              </span>
                            ))}
                          </span>
                        ) : (
                          visibleCue.map((w, i) => (
                            <span
                              key={`${w.start}-${w.word}`}
                              style={{
                                color:
                                  words.indexOf(w) === activeWordIndex
                                    ? captionStyle.activeTextColor
                                    : captionStyle.textColor,
                                fontSize: captionFontSize,
                                lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                                fontFamily: captionStyle.fontFamily,
                                fontWeight: captionStyle.fontWeight,
                                textShadow: captionStyle.textShadow
                                  ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                                  : undefined,
                                WebkitTextStroke: captionStyle.textOutline
                                  ? `1.5px ${captionStyle.outlineColor}`
                                  : undefined,
                                transition: "color 0.1s",
                              }}
                            >
                              {i > 0 ? " " : ""}
                              {w.word || "…"}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="shrink-0 px-6 py-2 bg-gray-950 select-none border-t border-gray-800">
          <div className="flex justify-between items-center mb-1">
            {selected !== null && selectedIndex !== null ? (
              <div className="flex items-center gap-3">
                <p className="text-[11px] text-gray-400 truncate max-w-[10rem]">
                  Adjusting: <span className="text-white font-medium">"{selected.word}"</span>
                </p>
                <TimeInput
                  label="Start"
                  color="text-teal-400"
                  value={selected.start}
                  onCommit={(seconds) => {
                    setEdge(selectedIndex, "start", seconds);
                    seekTo(seconds);
                  }}
                />
                <TimeInput
                  label="End"
                  color="text-red-400"
                  value={selected.end}
                  onCommit={(seconds) => {
                    setEdge(selectedIndex, "end", seconds);
                    seekTo(seconds);
                  }}
                />
              </div>
            ) : (
              <p className="text-[11px] text-gray-500">Click a word to adjust its timing</p>
            )}
            <div className="text-[11px] font-mono text-gray-400">
              <span className="text-white">{fmtTime(currentTime)}</span> / {fmtTime(duration)}
            </div>
          </div>

          <div
            ref={timelineRef}
            onMouseDown={handleTimelineClick}
            className="relative h-6 bg-gray-800 rounded-md cursor-pointer overflow-hidden group"
          >
            {/* Visual Indicator: Safe Zone for dragging */}
            {selected !== null && (
              <div
                className="absolute inset-y-0 bg-white/5 pointer-events-none border-x border-dashed border-white/20"
                style={{
                  left: `${timeToPercent(editableFrom ?? 0)}%`,
                  width: `${timeToPercent(duration - (editableFrom ?? 0))}%`,
                }}
              />
            )}

            {/* Played region */}
            <div
              className="absolute inset-y-0 left-0 bg-primary-500/40 pointer-events-none"
              style={{ width: `${timeToPercent(currentTime)}%` }}
            />

            {/* Word ticks */}
            {words.map((w, i) => (
              <div
                key={`tick-${w.start}-${i}`}
                className="absolute top-1/2 -translate-y-1/2 h-3 w-px bg-white/30 pointer-events-none"
                style={{ left: `${timeToPercent(w.start)}%` }}
              />
            ))}

            {/* Selected range highlight */}
            {selected !== null && (
              <div
                className="absolute inset-y-0 bg-primary-500/40 border-y border-primary-400/50 pointer-events-none"
                style={{
                  left: `${timeToPercent(selected.start)}%`,
                  width: `${timeToPercent(selected.end - selected.start)}%`,
                }}
              />
            )}

            {/* Playhead */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none z-20 shadow-[0_0_8px_rgba(255,255,255,0.8)]"
              style={{ left: `${timeToPercent(currentTime)}%` }}
            />

            {/* Start handle */}
            {selected !== null && (
              <div
                className="absolute top-0 bottom-0 w-6 flex items-center justify-center cursor-ew-resize z-30"
                style={{ left: `${handleStartPct}%`, transform: "translateX(-50%)" }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDragging("start");
                }}
              >
                <div className="w-1 h-full bg-teal-400 relative">
                  <div className="absolute top-1/2 -translate-y-1/2 -left-1.5 w-4 h-4 rounded-full bg-teal-400 shadow-md border-2 border-gray-900" />
                </div>
              </div>
            )}

            {/* End handle */}
            {selected !== null && (
              <div
                className="absolute top-0 bottom-0 w-6 flex items-center justify-center cursor-ew-resize z-30"
                style={{ left: `${handleEndPct}%`, transform: "translateX(-50%)" }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDragging("end");
                }}
              >
                <div className="w-1 h-full bg-red-400 relative">
                  <div className="absolute top-1/2 -translate-y-1/2 -left-1.5 w-4 h-4 rounded-full bg-red-400 shadow-md border-2 border-gray-900" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
          {lockWords ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm">
              Drag a word's start or end on the timeline, or type the times above it. The words come
              from the passage text — edit the passage to change them.
            </p>
          ) : (
            <button
              type="button"
              onClick={addAtEnd}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <HiPlus className="w-4 h-4" />
              Add Word at {fmtTime(currentTime)}
            </button>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSave(words)}
              disabled={isSaving}
              className="px-6 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {isSaving ? "Saving..." : "Save Subtitles"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
